import { useEffect, useRef, useState } from 'react';

import type { SubmissionData } from './exerciseTypes';

/** An exercise allows a first attempt and one more; after that the student must move on. */
export const MAX_ATTEMPTS = 2;
export const RETRY_COOLDOWN_SECONDS = 5;

function isFailedResult(submission: SubmissionData | null): submission is SubmissionData {
  return Boolean(submission) && (submission?.status === undefined || submission.status === 'DONE') && submission?.passed === false;
}

/**
 * Counts wrong answers during this visit to an exercise and runs the pause (inputs locked, with a
 * countdown) that follows a wrong answer while an attempt remains. Attempts are tracked in the
 * browser only; reloading the page starts over.
 */
export function useExerciseAttempts(submission: SubmissionData | null) {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const countedResultRef = useRef<unknown>(null);
  const failedAttemptsRef = useRef(0);

  useEffect(() => {
    if (!isFailedResult(submission)) {
      return;
    }
    const resultKey = submission.id ?? submission;
    if (countedResultRef.current === resultKey) {
      return;
    }
    countedResultRef.current = resultKey;
    failedAttemptsRef.current += 1;
    setFailedAttempts(failedAttemptsRef.current);
    if (failedAttemptsRef.current < MAX_ATTEMPTS) {
      setCooldownSeconds(RETRY_COOLDOWN_SECONDS);
    }
  }, [submission]);

  const isCoolingDown = cooldownSeconds > 0;
  useEffect(() => {
    if (!isCoolingDown) {
      return undefined;
    }
    const timer = window.setInterval(() => setCooldownSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [isCoolingDown]);

  return { failedAttempts, cooldownSeconds, attemptsExhausted: failedAttempts >= MAX_ATTEMPTS };
}
